import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { notificationsKey } from "@/notifications/api";

/* /api/portal/* — only the fields a client may see (no costs, rates or internal notes). */
export interface PortalTicket {
  id: string;
  number: string;
  title: string;
  type: string | null;
  priority: string;
  status: string;
  bau_solution: string | null;
  requested_by: string | null;
  assigned_to: string | null;
  requested_at: string;
  last_status_change_at: string | null;
  completed_at: string | null;
}

export interface PortalTicketDetail extends PortalTicket {
  detailed_requirement: string;
  solution_provided: string | null;
}

export interface PortalSolution {
  id: string;
  name: string;
  department: string | null;
}

export interface PortalProject {
  id: string;
  ref: string;
  name: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
}

export interface PortalInvoice {
  id: string;
  number: string;
  date: string;
  due_date: string | null;
  currency: string;
  gross_amount: number;
  outstanding: number;
}

export type TicketState = "open" | "closed" | "all";

const keys = {
  tickets: (state: TicketState) => ["portal", "tickets", state] as const,
  ticket: (id: string) => ["portal", "ticket", id] as const,
};

export const usePortalTickets = (state: TicketState) =>
  useQuery({ queryKey: keys.tickets(state), queryFn: () => apiFetch<PortalTicket[]>("/portal/tickets", { query: { state } }) });

export const usePortalTicket = (id: string | undefined) =>
  useQuery({
    queryKey: keys.ticket(id ?? ""),
    queryFn: () => apiFetch<PortalTicketDetail>(`/portal/tickets/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });

export const usePortalSolutions = () =>
  useQuery({ queryKey: ["portal", "bau-solutions"], queryFn: () => apiFetch<PortalSolution[]>("/portal/bau-solutions") });

export const usePortalProjects = () =>
  useQuery({ queryKey: ["portal", "projects"], queryFn: () => apiFetch<PortalProject[]>("/portal/projects") });

export const usePortalInvoices = () =>
  useQuery({ queryKey: ["portal", "invoices"], queryFn: () => apiFetch<PortalInvoice[]>("/portal/invoices") });

export interface NewTicket {
  title: string;
  detailed_requirement: string;
  bau_solution_id: string;
  priority: string;
}

export function useRaiseTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NewTicket) => apiFetch<PortalTicket>("/portal/tickets", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal", "tickets"] });
      void qc.invalidateQueries({ queryKey: notificationsKey });
    },
  });
}
